import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MapPin, Clock, Zap, FileText, ImageIcon, CheckCircle, AlertCircle, Plus, Calendar, Loader2 } from 'lucide-react';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import AssignProviderModal from '@/components/modals/AssignProviderModal';

interface JobDetail {
  id: string;
  customer: string;
  property: string;
  service: string;
  provider: string;
  status: 'Overdue' | 'Variation Pending' | 'In Progress' | 'Awaiting Assignment' | 'Quality Check';
  risk: 'High' | 'Medium' | 'Low' | null;
  description: string;
  location: string;
  value: string;
  scheduled: string;
  estimatedDuration: string;
  materialsRequired: string;
  nextAction?: string;
  nextActionButtons?: Array<{ label: string; action: string }>;
  propertyAccess: {
    key: string;
    location: string;
    contact: string;
    parking: string;
  };
  photos: string[];
  timeline: Array<{
    label: string;
    date: string;
    time: string;
    description?: string;
  }>;
  notes?: Array<{
    id: string;
    author: string;
    role: string;
    date: string;
    time: string;
    content: string;
    tags?: string[];
    attachments?: Array<{ name: string; size: string }>;
  }>;
  scope?: {
    originalScope: {
      initialLocation: string;
      primaryTask: string;
      allocatedDuration: string;
      materialsBudget: string;
      totalEstimated: string;
    };
    adjustedScope: {
      primaryTask: string;
      allocatedDuration: string;
      durationIncrease: string;
      materialsBudget: string;
      budgetIncrease: string;
      newAdjustedTotal: string;
      verified: boolean;
    };
    adjustmentNotes: Array<{
      author: string;
      role: string;
      date: string;
      time: string;
      note: string;
    }>;
    inspectionMedia: string[];
    jobLocation: string;
    primaryContact: string;
    primaryContactPhone: string;
    warrantyScope: string;
  };
  variations?: {
    totalImpact: number;
    approved: number;
    pending: number;
    rejected: number;
    requests: Array<{
      id: string;
      title: string;
      description: string;
      date: string;
      amount: number;
      status: 'Submitted' | 'Under Review' | 'Approved' | 'Rejected';
      icon: 'document' | 'image' | 'checkmark' | 'x';
      actionButton?: string;
      actionLabel?: string;
      managedBy?: string;
    }>;
  };
}

const JobDetailsPage = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [job, setJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // (audit 2026-06-11) — the Scope & Details tab now shows the originating
  // service-request data (the row at /admin/service-requests/:id, linked
  // via bookings.quote_id).
  const [serviceRequest, setServiceRequest] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [isAssignProviderOpen, setIsAssignProviderOpen] = useState(false);
  const [isReassignProviderOpen, setIsReassignProviderOpen] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parse jobId - can be in format JOB-17759, JV-17759, or just 17759
  const extractJobId = (id: string) => {
    const match = id.match(/\d+/);
    return match ? match[0] : id;
  };

  // Function to refresh job details
  const refreshJobDetails = async () => {
    if (!jobId) return;
    try {
      const numericId = extractJobId(jobId);
      const response = await apiService.request(`/admin/jobs-list?limit=1000`, 'GET');
      if (response?.success && response?.data?.jobs) {
        const foundJob = response.data.jobs.find((j: any) => 
          j.job_id === parseInt(numericId) || 
          j.job_code === jobId ||
          j.job_code === `JV-${numericId}`
        );
        
        if (foundJob) {
          const mappedJob = mapApiJobToDetail(foundJob);
          setJob(mappedJob);
          // Fetch updated notes
          try {
            const notesResponse = await apiService.request(`/jobs/${jobId}/notes`, 'GET');
            if (notesResponse?.success && Array.isArray(notesResponse.data)) {
              setJob(prev => prev ? { ...prev, notes: notesResponse.data } : null);
            }
          } catch (notesErr) {
            console.error('Failed to fetch notes:', notesErr);
          }
          // Fetch updated timeline
          try {
            const timelineResponse = await apiService.request(`/jobs/${jobId}/timeline`, 'GET');
            if (timelineResponse?.success && Array.isArray(timelineResponse.data)) {
              setJob(prev => prev ? { ...prev, timeline: timelineResponse.data } : null);
            }
          } catch (timelineErr) {
            console.error('Failed to fetch timeline:', timelineErr);
          }
        }
      }
    } catch (err) {
      console.error('Failed to refresh job:', err);
    }
  };

  // Function to fetch notes for a job
  const fetchJobNotes = async (jobCode: string, jobToUpdate?: JobDetail | null) => {
    try {
      const response = await apiService.request(`/jobs/${jobCode}/notes`, 'GET');
      if (response?.success && Array.isArray(response.data)) {
        if (jobToUpdate) {
          jobToUpdate.notes = response.data;
          setJob(jobToUpdate);
        } else if (job) {
          setJob({ ...job, notes: response.data });
        }
      }
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    }
  };

  // Fetch job from API
  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) return;
      setLoading(true);
      try {
        const numericId = extractJobId(jobId);
        const response = await apiService.request(`/admin/jobs-list?limit=1000`, 'GET');
        if (response?.success && response?.data?.jobs) {
          // Find job by ID or job_code
          const foundJob = response.data.jobs.find((j: any) => 
            j.job_id === parseInt(numericId) || 
            j.job_code === jobId ||
            j.job_code === `JV-${numericId}`
          );
          
          if (foundJob) {
            const mappedJob = mapApiJobToDetail(foundJob);
            setJob(mappedJob);
            setError(null);
            // Fetch the originating service request for the Scope tab.
            try {
              const srResponse = await apiService.request(
                `/admin/jobs/${numericId}/service-request`, 'GET'
              );
              if (srResponse?.success && srResponse.data) {
                setServiceRequest(srResponse.data);
              }
            } catch (srErr) {
              // Booking may not have come from a service request — silent.
            }
            // Fetch notes for this job
            try {
              const notesResponse = await apiService.request(`/jobs/${jobId}/notes`, 'GET');
              if (notesResponse?.success && Array.isArray(notesResponse.data)) {
                setJob(prev => prev ? { ...prev, notes: notesResponse.data } : null);
              }
            } catch (notesErr) {
              console.error('Failed to fetch notes:', notesErr);
              // Don't fail the entire job load if notes fail
            }
            // Fetch timeline for this job
            try {
              const timelineResponse = await apiService.request(`/jobs/${jobId}/timeline`, 'GET');
              if (timelineResponse?.success && Array.isArray(timelineResponse.data)) {
                setJob(prev => prev ? { ...prev, timeline: timelineResponse.data } : null);
              }
            } catch (timelineErr) {
              console.error('Failed to fetch timeline:', timelineErr);
              // Don't fail the entire job load if timeline fails
            }
          } else {
            setError('Job not found');
          }
        } else {
          setError('Failed to load jobs');
        }
      } catch (err) {
        console.error('Failed to fetch job:', err);
        setError('Failed to load job details');
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [jobId]);

  // Map API job response to JobDetail interface
  const mapApiJobToDetail = (apiJob: any): JobDetail => {
    const getNextAction = (status: string) => {
      switch(status) {
        case 'Awaiting Assignment':
          return 'Assign a qualified provider to this job.';
        case 'In Progress':
          return 'Awaiting provider completion photos for scope verification.';
        case 'Quality Check':
          return 'Review quality and approve job completion.';
        case 'Variation Pending':
          return 'Review and approve pending variation request.';
        case 'Overdue':
          return 'Take action on overdue job - escalation may be required.';
        default:
          return 'Check job status and required actions.';
      }
    };

    const status = apiJob.status || 'Awaiting Assignment';
    
    return {
      id: apiJob.job_code || `JOB-${apiJob.job_id}`,
      customer: apiJob.customer_name || 'Unknown',
      property: apiJob.property || 'N/A',
      service: apiJob.service_name || 'Service',
      provider: apiJob.provider_name || 'Unassigned',
      status: status,
      risk: null,
      description: 'Job details available in booking system',
      location: apiJob.postcode || 'N/A',
      value: apiJob.total_amount_pence ? `£${(apiJob.total_amount_pence / 100).toFixed(2)}` : 'TBD',
      scheduled: apiJob.booking_date ? `${new Date(apiJob.booking_date).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })}, ${apiJob.time}` : 'TBD',
      estimatedDuration: 'N/A',
      materialsRequired: 'N/A',
      nextAction: getNextAction(status),
      nextActionButtons: status === 'In Progress' 
        ? [
            { label: 'Remind Provider', action: 'remind' },
            { label: 'View Requirements', action: 'view' }
          ]
        : status === 'Quality Check'
        ? [
            { label: 'Approve Completion', action: 'approve' },
            { label: 'Request Changes', action: 'request' }
          ]
        : [
            { label: 'Take Action', action: 'action' }
          ],
      propertyAccess: {
        key: 'TBD',
        location: 'TBD',
        contact: 'TBD',
        parking: 'TBD',
      },
      photos: [],
      timeline: [
        { label: 'Job Created', date: new Date(apiJob.booking_date).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }), time: apiJob.time || '09:00 AM' },
      ],
      notes: [],
    };
  };

  // Action Handlers

  // Action Handlers
  const handleAssign = async () => {
    setIsAssignProviderOpen(true);
  };

  const handleAssignProviderSubmit = async (providerId: string, notes: string, notifications: { app: boolean; sms: boolean }) => {
    setActionInProgress('assign');
    try {
      const response = await apiService.request(
        `/admin/jobs/${job?.id}/assign-provider`,
        'POST',
        { providerId }
      );
      
      if (response.success) {
        setActionMessage({ type: 'success', text: response.message || 'Provider assigned successfully!' });
        // Refresh job details after successful assignment
        await refreshJobDetails();
        setIsAssignProviderOpen(false);
      } else {
        setActionMessage({ type: 'error', text: response.message || 'Failed to assign provider' });
        setTimeout(() => setActionInProgress(null), 2000);
      }
    } catch (err) {
      console.error('Error assigning provider:', err);
      setActionMessage({ type: 'error', text: 'Failed to assign provider. Please try again.' });
      setTimeout(() => setActionInProgress(null), 2000);
    }
  };

  const handleReassign = async () => {
    setIsReassignProviderOpen(true);
  };

  const handleReassignProviderSubmit = async (providerId: string, notes: string, notifications: { app: boolean; sms: boolean }) => {
    setActionInProgress('reassign');
    try {
      const response = await apiService.request(
        `/admin/jobs/${job?.id}/reassign-provider`,
        'POST',
        { providerId }
      );
      
      if (response.success) {
        setActionMessage({ type: 'success', text: response.message || 'Provider reassigned successfully!' });
        // Refresh job details after successful reassignment
        await refreshJobDetails();
        setIsReassignProviderOpen(false);
      } else {
        setActionMessage({ type: 'error', text: response.message || 'Failed to reassign provider' });
        setTimeout(() => setActionInProgress(null), 2000);
      }
    } catch (err) {
      console.error('Error reassigning provider:', err);
      setActionMessage({ type: 'error', text: 'Failed to reassign provider. Please try again.' });
      setTimeout(() => setActionInProgress(null), 2000);
    }
  };

  const handleApproveCompletion = async () => {
    setActionInProgress('approve');
    try {
      const response = await apiService.request(
        `/admin/jobs/${job?.id}/approve`,
        'POST'
      );
      
      if (response.success) {
        setActionMessage({ type: 'success', text: response.message || 'Job completion approved successfully!' });
        // Refresh job details after successful approval
        await refreshJobDetails();
      } else {
        setActionMessage({ type: 'error', text: response.message || 'Failed to approve job completion' });
        setTimeout(() => setActionInProgress(null), 2000);
      }
    } catch (err) {
      console.error('Error approving completion:', err);
      setActionMessage({ type: 'error', text: 'Failed to approve completion. Please try again.' });
      setTimeout(() => setActionInProgress(null), 2000);
    }
  };

  const handleReleasePayout = async () => {
    setActionInProgress('payout');
    try {
      const response = await apiService.request(
        `/admin/jobs/${job?.id}/release-payout`,
        'POST'
      );
      
      if (response.success) {
        setActionMessage({ type: 'success', text: response.message || 'Payout released successfully!' });
        // Refresh job details after successful payout release
        await refreshJobDetails();
      } else {
        setActionMessage({ type: 'error', text: response.message || 'Failed to release payout' });
        setTimeout(() => setActionInProgress(null), 2000);
      }
    } catch (err) {
      console.error('Error releasing payout:', err);
      setActionMessage({ type: 'error', text: 'Failed to release payout. Please try again.' });
      setTimeout(() => setActionInProgress(null), 2000);
    }
  };

  const handleRequestScopeReview = async () => {
    if (!job?.id) return;
    setActionInProgress('scope');
    try {
      const response = await apiService.request(`/admin/jobs/${job.id}/scope-review`, 'POST', {
        requestedAt: new Date().toISOString(),
      });
      if (response?.success) {
        setActionMessage({ type: 'success', text: 'Scope review request sent to provider.' });
      } else {
        setActionMessage({ type: 'error', text: response?.message || 'Failed to request scope review.' });
      }
    } catch (err) {
      console.error('Error requesting scope review:', err);
      setActionMessage({ type: 'error', text: 'Failed to request scope review. Please try again.' });
    } finally {
      setTimeout(() => setActionInProgress(null), 2000);
    }
  };

  // (audit 2026-06-11) — Adjusted-scope adjudication handlers, plus
  // export/utility helpers for variation history, notes and per-doc rows.
  const handleScopeDecision = async (decision: 'approve' | 'flag-audit' | 'reject') => {
    if (!job?.id) return;
    const confirmMsg = decision === 'approve' ? 'Approve the adjusted scope?'
      : decision === 'flag-audit' ? 'Flag the adjusted scope for audit?'
      : 'Reject the proposed adjustments?';
    if (!window.confirm(confirmMsg)) return;
    setActionInProgress('scopeDecision');
    try {
      const response = await apiService.request(`/admin/jobs/${job.id}/scope/${decision}`, 'POST', {});
      if (response?.success) {
        setActionMessage({ type: 'success', text: response.message || 'Decision recorded' });
        if (typeof refreshJobDetails === 'function') await refreshJobDetails();
      } else {
        setActionMessage({ type: 'error', text: response?.message || 'Action failed' });
      }
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err?.message || 'Network error' });
    } finally {
      setTimeout(() => setActionInProgress(null), 1500);
    }
  };

  const exportArrayAsCSV = (rows: any[], filename: string) => {
    if (!rows || rows.length === 0) {
      alert('Nothing to export.');
      return;
    }
    const headers = Array.from(rows.reduce((s: Set<string>, r) => {
      Object.keys(r || {}).forEach((k) => s.add(k));
      return s;
    }, new Set<string>()));
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => escape((r as any)[h])).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportVariationHistory = () => {
    const rows = (job?.variations || job?.variation_history || []) as any[];
    exportArrayAsCSV(rows, `variation-history-${job?.id || 'job'}.csv`);
  };

  const handleEditNotes = () => {
    // Scroll to / focus the note composer textarea, which already exists
    // further down the notes tab. Falls back to an alert if not present.
    const ta = document.querySelector<HTMLTextAreaElement>('textarea[data-note-composer]');
    if (ta) {
      ta.focus();
      ta.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setActiveTab('notes');
    }
  };

  const handleExportNotes = () => {
    const rows = (job?.notes || job?.note_list || []) as any[];
    exportArrayAsCSV(rows, `notes-${job?.id || 'job'}.csv`);
  };

  const handleOpenLocation = () => {
    const addr = job?.location || job?.property_address;
    if (!addr) {
      alert('No address on file for this job.');
      return;
    }
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`, '_blank', 'noopener');
  };

  const handleNoteAttachment = () => {
    const input = document.querySelector<HTMLInputElement>('input[data-note-attach-input]');
    if (input) input.click();
  };

  const handleNoteMention = () => {
    setNoteContent((c) => (c.endsWith('@') ? c : c + ' @'));
  };

  const handleDocView = (doc: any) => {
    const url = doc?.file_url || doc?.url || doc?.download_url;
    if (!url) {
      alert('No file URL available for this document.');
      return;
    }
    window.open(url, '_blank', 'noopener');
  };

  const handleDocDownload = (doc: any) => {
    const url = doc?.file_url || doc?.url || doc?.download_url;
    if (!url) {
      alert('No file URL available for this document.');
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = doc?.file_name || doc?.filename || 'document';
    a.click();
  };

  const handleOpenGallery = () => {
    setActiveTab('photos');
  };

  const handleDynamicAction = (req: any) => {
    if (!req) return;
    // The dynamic actionButton on variations rows — emit an alert with the
    // label so admin notices it's been clicked. Specific behaviour can be
    // mapped here once each per-row action is defined server-side.
    if (req.actionUrl) {
      navigate(req.actionUrl);
    } else if (req.actionTab) {
      setActiveTab(req.actionTab);
    } else {
      alert(`Action: ${req.actionButton || 'No action defined'}`);
    }
  };

  const handleReviewVariation = async () => {
    setActionInProgress('variation');
    try {
      setActionMessage({ type: 'info', text: 'Opening variation review panel...' });
      setActiveTab('variations');
    } catch (err) {
      console.error('Error opening variations:', err);
      setActionMessage({ type: 'error', text: 'Failed to open variations. Please try again.' });
    } finally {
      setTimeout(() => setActionInProgress(null), 1000);
    }
  };

  const handleEscalateJob = async () => {
    setActionInProgress('escalate');
    try {
      const reason = prompt('Enter escalation reason:');
      if (!reason) {
        setActionInProgress(null);
        return;
      }
      
      const response = await apiService.request(
        `/admin/jobs/${job?.id}/escalate`,
        'POST',
        { reason }
      );
      
      if (response.success) {
        setActionMessage({ type: 'success', text: response.message || 'Job escalated to support team!' });
        // Refresh job details after successful escalation
        await refreshJobDetails();
      } else {
        setActionMessage({ type: 'error', text: response.message || 'Failed to escalate job' });
        setTimeout(() => setActionInProgress(null), 2000);
      }
    } catch (err) {
      console.error('Error escalating job:', err);
      setActionMessage({ type: 'error', text: 'Failed to escalate job. Please try again.' });
      setTimeout(() => setActionInProgress(null), 2000);
    }
  };

  const handlePauseJob = async () => {
    setActionInProgress('pause');
    try {
      const response = await apiService.request(
        `/admin/jobs/${job?.id}/pause`,
        'POST'
      );
      
      if (response.success) {
        setActionMessage({ type: 'success', text: response.message || 'Job paused successfully!' });
        // Refresh job details after successful pause
        await refreshJobDetails();
      } else {
        setActionMessage({ type: 'error', text: response.message || 'Failed to pause job' });
        setTimeout(() => setActionInProgress(null), 2000);
      }
    } catch (err) {
      console.error('Error pausing job:', err);
      setActionMessage({ type: 'error', text: 'Failed to pause job. Please try again.' });
      setTimeout(() => setActionInProgress(null), 2000);
    }
  };

  const handleCancelJob = async () => {
    if (window.confirm('Are you sure you want to cancel this job? This action cannot be undone.')) {
      setActionInProgress('cancel');
      try {
        const reason = prompt('Enter cancellation reason:');
        if (!reason) {
          setActionInProgress(null);
          return;
        }
        
        const response = await apiService.request(
          `/admin/jobs/${job?.id}/cancel`,
          'POST',
          { reason }
        );
        
        if (response.success) {
          setActionMessage({ type: 'success', text: response.message || 'Job cancelled successfully!' });
          // Refresh job details after successful cancellation
          await refreshJobDetails();
        } else {
          setActionMessage({ type: 'error', text: response.message || 'Failed to cancel job' });
          setTimeout(() => setActionInProgress(null), 2000);
        }
      } catch (err) {
        console.error('Error cancelling job:', err);
        setActionMessage({ type: 'error', text: 'Failed to cancel job. Please try again.' });
        setTimeout(() => setActionInProgress(null), 2000);
      }
    }
  };

  const handleContactSupport = () => {
    // (audit 2026-06-09) — was a stub that only set a message and did nothing.
    // Now navigates to the appropriate support page for the current role,
    // pre-filled with the active job id so the form references it. Role is
    // derived from the current URL prefix (no auth hook in this file).
    const jid = job?.id || jobId;
    const path = window.location.pathname;
    if (path.startsWith('/admin/')) {
      navigate(`/admin/support-disputes?jobId=${jid || ''}`);
    } else if (path.startsWith('/provider/')) {
      navigate(`/provider/support?jobId=${jid || ''}`);
    } else {
      navigate(`/dashboard/help-support?jobId=${jid || ''}`);
    }
  };

  const handleUploadDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !jobId) return;
    
    setIsUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('jobId', jobId);
      
      const response = await apiService.request(`/jobs/${jobId}/documents`, 'POST', formData);
      
      if (response?.success) {
        setActionMessage({ type: 'success', text: 'Document uploaded successfully' });
        refreshJobDetails();
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setActionMessage({ type: 'error', text: 'Failed to upload document' });
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      setActionMessage({ type: 'error', text: 'Error uploading document. Please try again.' });
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const handleUploadDocumentClick = () => {
    fileInputRef.current?.click();
  };

  const handleSaveNote = async () => {
    if (!noteContent.trim() || !jobId) {
      setActionMessage({ type: 'error', text: 'Please enter a note' });
      return;
    }

    setIsSavingNote(true);
    try {
      const newNote = {
        content: noteContent,
        jobId: jobId,
      };

      const response = await apiService.request(`/jobs/${jobId}/notes`, 'POST', newNote);

      if (response?.success) {
        setActionMessage({ type: 'success', text: 'Note saved successfully' });
        setNoteContent('');
        refreshJobDetails();
        setTimeout(() => setActionMessage(null), 3000);
      } else {
        setActionMessage({ type: 'error', text: 'Failed to save note' });
      }
    } catch (err) {
      console.error('Error saving note:', err);
      setActionMessage({ type: 'error', text: 'Error saving note. Please try again.' });
    } finally {
      setIsSavingNote(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900">Loading job details...</h2>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !job) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-gray-50 p-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900">Job not found</h2>
            <p className="text-gray-600 mt-2">{error || 'The job you are looking for does not exist.'}</p>
            <Button onClick={() => navigate('/admin/orders-jobs')} className="mt-4">
              Back to Jobs
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Overdue':
        return 'bg-red-100 text-red-700';
      case 'Variation Pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Awaiting Assignment':
        return 'bg-gray-100 text-gray-700';
      case 'Quality Check':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <>
      <Helmet>
        <title>Job {job.id} - Virewire</title>
      </Helmet>

      <AdminLayout>
        <div className="min-h-screen bg-gray-50">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 p-8">
            <Button
              onClick={() => navigate('/admin/orders-jobs')}
              variant="ghost"
              className="mb-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Orders & Jobs
            </Button>

            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">#{job.id}</h1>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
                      {job.status}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{job.service}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    {job.location}
                  </p>
                </div>
              </div>

              {/* Key Details Row — Scheduled column dropped (was always "TBD"
                  because backend booking_date is null on most rows). */}
              <div className="grid grid-cols-3 gap-6 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-xs uppercase font-semibold text-gray-600 mb-1">CUSTOMER</p>
                  <p className="text-sm font-medium text-gray-900">{job.customer}</p>
                </div>
                <div>
                  <p className="text-xs uppercase font-semibold text-gray-600 mb-1">PROVIDER</p>
                  <p className="text-sm font-medium text-teal-600">{job.provider}</p>
                </div>
                <div>
                  <p className="text-xs uppercase font-semibold text-gray-600 mb-1">VALUE</p>
                  <p className="text-sm font-bold text-gray-900">{job.value}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-8">
            {/* Action Feedback Message */}
            {actionMessage && (
              <div className={`mb-6 p-4 rounded-lg border flex items-start gap-3 ${
                actionMessage.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
                actionMessage.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
                'bg-blue-50 border-blue-200 text-blue-800'
              }`}>
                {actionMessage.type === 'success' && <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                {actionMessage.type === 'error' && <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                {actionMessage.type === 'info' && <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <p className="text-sm">{actionMessage.text}</p>
                <button 
                  onClick={() => setActionMessage(null)}
                  className="ml-auto text-lg"
                >
                  ×
                </button>
              </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="border-b border-gray-200">
                <div className="flex gap-8 px-8 py-4">
                  {/* (audit 2026-06-11) — all per-tab panels are folded into
                      Overview, so the page renders only the Overview tab.
                      Other panel JSX remains conditional below to keep
                      backwards-compat with handlers that setActiveTab(...). */}
                  {['overview'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-2 px-1 font-medium text-sm border-b-2 transition-colors ${
                        activeTab === tab
                          ? 'text-teal-600 border-teal-600'
                          : 'text-gray-600 border-transparent hover:text-gray-900'
                      }`}
                    >
                      {tab === 'overview' && 'Overview'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-8">
                {activeTab === 'overview' && (
                  <div className="grid grid-cols-3 gap-8">
                    {/* Left Column */}
                    <div className="col-span-2 space-y-6">
                      {/* (audit 2026-06-12) — "Next Platform Action" banner
                          removed per product request — its content was a
                          placeholder ("Check job status and required actions",
                          status "SCHEDULED – TBD") and the buttons duplicated
                          actions already available in the Platform Actions
                          sidebar. */}

                      {/* (audit 2026-06-11) — Service Details + Property Access
                          were stub fields. Replaced with real data from the
                          originating service_request (linked via bookings.quote_id). */}
                      {serviceRequest ? (
                        <>
                          {/* Service Request Header */}
                          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center">
                                  <FileText className="w-5 h-5 text-teal-700" />
                                </div>
                                <div>
                                  <p className="text-xs uppercase font-bold text-gray-500 tracking-widest">Originating Service Request</p>
                                  <p className="text-sm font-mono text-gray-900 mt-0.5">{serviceRequest.id}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-block px-3 py-1 bg-teal-50 text-teal-700 text-xs font-bold uppercase tracking-wider rounded-full border border-teal-200">
                                  {serviceRequest.status || 'pending'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Service + Customer cards */}
                          <div className="grid grid-cols-2 gap-6">
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                              <h4 className="text-xs uppercase font-bold text-gray-500 mb-5 tracking-widest">
                                Service Details
                              </h4>
                              <dl className="space-y-4">
                                <div>
                                  <dt className="text-xs text-gray-500 font-medium mb-1">Category</dt>
                                  <dd className="text-sm font-semibold text-gray-900">{serviceRequest.service_category_name || serviceRequest.service_category || '—'}</dd>
                                </div>
                                <div>
                                  <dt className="text-xs text-gray-500 font-medium mb-1">Service Type</dt>
                                  <dd className="text-sm font-semibold text-gray-900">
                                    {serviceRequest.service_name || serviceRequest.service_type || '—'}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-xs text-gray-500 font-medium mb-1">Property Type</dt>
                                  <dd className="text-sm font-semibold text-gray-900 capitalize">{serviceRequest.property_type || '—'}</dd>
                                </div>
                                <div>
                                  <dt className="text-xs text-gray-500 font-medium mb-1">Postcode</dt>
                                  <dd className="text-sm font-semibold text-gray-900 font-mono">{serviceRequest.postcode || '—'}</dd>
                                </div>
                                <div className="border-t border-gray-100 pt-4 mt-4">
                                  <dt className="text-xs text-gray-500 font-medium mb-1">Quoted Price</dt>
                                  <dd className="text-3xl font-bold text-gray-900">
                                    {serviceRequest.quoted_price != null
                                      ? `£${Number(serviceRequest.quoted_price).toFixed(2)}`
                                      : '—'}
                                  </dd>
                                  {serviceRequest.quoted_date && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Quoted {new Date(serviceRequest.quoted_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </p>
                                  )}
                                </div>
                              </dl>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                              <h4 className="text-xs uppercase font-bold text-gray-500 mb-5 tracking-widest">
                                Customer Contact
                              </h4>
                              <dl className="space-y-4">
                                <div>
                                  <dt className="text-xs text-gray-500 font-medium mb-1">Name</dt>
                                  <dd className="text-sm font-semibold text-gray-900">
                                    {serviceRequest.contact_name || serviceRequest.customer_name_full || '—'}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-xs text-gray-500 font-medium mb-1">Email</dt>
                                  <dd className="text-sm font-semibold text-gray-900 break-all">
                                    {serviceRequest.contact_email
                                      ? <a href={`mailto:${serviceRequest.contact_email}`} className="text-teal-700 hover:underline">{serviceRequest.contact_email}</a>
                                      : '—'}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-xs text-gray-500 font-medium mb-1">Phone</dt>
                                  <dd className="text-sm font-semibold text-gray-900">
                                    {serviceRequest.contact_phone
                                      ? <a href={`tel:${serviceRequest.contact_phone}`} className="text-teal-700 hover:underline">{serviceRequest.contact_phone}</a>
                                      : '—'}
                                  </dd>
                                </div>
                                {serviceRequest.company && (
                                  <div>
                                    <dt className="text-xs text-gray-500 font-medium mb-1">Company</dt>
                                    <dd className="text-sm font-semibold text-gray-900">{serviceRequest.company}</dd>
                                  </div>
                                )}
                                <div className="border-t border-gray-100 pt-4 mt-4">
                                  <dt className="text-xs text-gray-500 font-medium mb-1">Submitted</dt>
                                  <dd className="text-sm font-semibold text-gray-900">
                                    {serviceRequest.created_at
                                      ? new Date(serviceRequest.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
                                      : '—'}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                          </div>

                          {/* Request Details (parsed JSON as a clean grid) */}
                          {serviceRequest.details && (() => {
                            let parsed: Record<string, any> = {};
                            try { parsed = JSON.parse(serviceRequest.details); }
                            catch { parsed = { details: serviceRequest.details }; }
                            const entries = Object.entries(parsed);
                            if (entries.length === 0) return null;
                            const humanize = (k: string) =>
                              k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                            return (
                              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <h4 className="text-xs uppercase font-bold text-gray-500 mb-5 tracking-widest">
                                  Request Specification
                                </h4>
                                <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                                  {entries.map(([k, v]) => (
                                    <div key={k}>
                                      <dt className="text-xs text-gray-500 font-medium mb-1">{humanize(k)}</dt>
                                      <dd className="text-sm font-semibold text-gray-900">{String(v ?? '—')}</dd>
                                    </div>
                                  ))}
                                </dl>
                              </div>
                            );
                          })()}

                          {serviceRequest.notes && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                              <h4 className="text-xs uppercase font-bold text-yellow-800 tracking-widest mb-2">
                                Internal Notes
                              </h4>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{serviceRequest.notes}</p>
                            </div>
                          )}
                        </>
                      ) : (
                        // Fallback: original placeholder cards when no SR is linked.
                        <div className="grid grid-cols-2 gap-6">
                          <div className="bg-gray-50 rounded-lg p-6">
                            <h4 className="text-xs uppercase font-bold text-gray-600 mb-4">SERVICE DETAILS</h4>
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs text-gray-600 font-semibold mb-1">Primary Task</p>
                                <p className="text-sm font-semibold text-gray-900">{job.service}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 font-semibold mb-1">Estimated Duration</p>
                                <p className="text-sm font-semibold text-gray-900">{job.estimatedDuration}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 font-semibold mb-1">Materials Required</p>
                                <p className="text-sm font-semibold text-gray-900">{job.materialsRequired}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-6">
                            <h4 className="text-xs uppercase font-bold text-gray-600 mb-4">PROPERTY ACCESS</h4>
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs text-gray-600 font-semibold mb-1">Key Location</p>
                                <p className="text-sm font-semibold text-gray-900">{job.propertyAccess.key}</p>
                                <p className="text-xs text-gray-600">({job.propertyAccess.location})</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 font-semibold mb-1">On-site Contact</p>
                                <p className="text-sm font-semibold text-gray-900">{job.propertyAccess.contact}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 font-semibold mb-1">Parking</p>
                                <p className="text-sm font-semibold text-gray-900">{job.propertyAccess.parking}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Site Photos */}
                      {job.photos.length > 0 && (
                        <div>
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="text-xs uppercase font-bold text-gray-600">SITE PHOTOS ({job.photos.length})</h4>
                            <button
                              onClick={handleOpenGallery}
                              className="text-xs text-teal-600 font-medium hover:text-teal-700"
                            >
                              View Gallery →
                            </button>
                          </div>
                          <div className="grid grid-cols-4 gap-3">
                            {job.photos.map((photo, idx) => (
                              <img key={idx} src={photo} alt={`Site ${idx + 1}`} className="w-full h-24 rounded-lg object-cover" />
                            ))}
                            {job.photos.length < 4 && (
                              <div className="w-full h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:bg-gray-50">
                                <ImageIcon className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column - Platform Actions */}
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <p className="text-xs uppercase font-bold text-gray-600 mb-4 flex items-center gap-2">
                          <FileText className="w-4 h-4" /> PLATFORM ACTIONS
                        </p>
                        <div className="space-y-2">
                          {(job?.provider === 'Unassigned' || job?.provider === 'N/A') && (
                            <Button 
                              onClick={handleAssign}
                              disabled={actionInProgress === 'assign'}
                              className="w-full justify-start text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                            >
                              👤 Assign Provider
                            </Button>
                          )}
                          {job?.provider && job?.provider !== 'Unassigned' && job?.provider !== 'N/A' && (
                            <Button 
                              onClick={handleReassign}
                              disabled={actionInProgress === 'reassign'}
                              className="w-full justify-start text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                            >
                              🔄 Reassign Provider
                            </Button>
                          )}
                          <Button 
                            onClick={handleApproveCompletion}
                            disabled={actionInProgress === 'approve' || job?.status === 'Completed' || job?.status === 'completed'}
                            className="w-full justify-start bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {actionInProgress === 'approve' ? 'Approving...' : job?.status === 'Completed' || job?.status === 'completed' ? 'Approved' : 'Approve Completion'}
                          </Button>




                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <Button 
                          onClick={handlePauseJob}
                          disabled={actionInProgress === 'pause'}
                          className="w-full justify-center text-gray-600 text-sm disabled:opacity-50" 
                          variant="ghost"
                        >
                          PAUSE JOB
                        </Button>
                        <Button 
                          onClick={handleCancelJob}
                          disabled={actionInProgress === 'cancel'}
                          className="w-full justify-center text-gray-600 text-sm mt-2 disabled:opacity-50" 
                          variant="ghost"
                        >
                          CANCEL JOB
                        </Button>
                        <Button 
                          onClick={handleContactSupport}
                          disabled={actionInProgress === 'support'}
                          className="w-full justify-center text-teal-600 text-sm mt-4 font-medium disabled:opacity-50" 
                          variant="ghost"
                        >
                          Contact Support Team
                        </Button>
                      </div>

                      {/* Job Summary */}
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="text-xs uppercase font-bold text-gray-600 mb-4">JOB SUMMARY</h4>
                        <div className="space-y-4">
                          <div>
                            <p className="text-xs uppercase font-semibold text-gray-600 mb-1">CREATED DATE</p>
                            <p className="text-sm text-gray-700">
                              {job?.createdAt ? new Date(job.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </p>
                          </div>
                          <div className="border-t pt-4">
                            <p className="text-xs uppercase font-semibold text-gray-600 mb-2">JOB VALUE</p>
                            <p className="text-lg font-bold text-gray-900">{job?.value || '—'}</p>
                          </div>
                          <div className="border-t pt-4">
                            <div className="flex items-start gap-2 text-xs text-gray-600">
                              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <span>Scheduled: {job?.scheduled || '—'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'timeline' && (
                  <div className="space-y-8">
                    {/* Header */}
                    <div>
                      <p className="text-xs uppercase font-semibold text-gray-600 mb-2">AUDIT EVENT LOG</p>
                      <h3 className="text-3xl font-bold text-gray-900 mb-3">Timeline</h3>
                      <p className="text-sm text-gray-600">A complete vertical audit trail of all actions, state changes, and communications for Job #{job.id}.</p>
                    </div>

                    {/* Info Alert */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-semibold text-blue-900">Timeline Data</p>
                        <p className="text-blue-700 text-xs mt-1">Job status and key milestones are tracked here. Event details will be populated as actions occur in the system.</p>
                      </div>
                    </div>

                    {/* Timeline Events */}
                    {job.timeline && job.timeline.length > 0 ? (
                      <div className="space-y-6">
                        {job.timeline.map((event, idx) => (
                          <div key={idx} className="flex gap-0 pb-6 border-b border-gray-200">
                            {/* Left Sidebar */}
                            <div className="flex flex-col items-center pr-8 w-32 flex-shrink-0">
                              <p className="text-xs text-gray-600 text-center mb-4 font-semibold">{event.date}</p>
                              <CheckCircle className="w-6 h-6 text-teal-500 mb-4" />
                            </div>
                            
                            {/* Vertical Line */}
                            <div className="w-1 bg-teal-400 flex-shrink-0 mr-8"></div>
                            
                            {/* Event Content */}
                            <div className="pb-4 flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-semibold text-gray-900">{event.label}</p>
                                <p className="text-xs text-gray-600">{event.time}</p>
                              </div>
                              <p className="text-sm text-gray-600">
                                {event.description || `Event recorded at ${event.date} ${event.time}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-8 text-center">
                        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600 text-sm">No timeline events recorded yet</p>
                        <p className="text-gray-500 text-xs mt-1">Timeline data will appear as job status changes</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'scope' && serviceRequest && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-teal-600" />
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Originating Service Request</h3>
                          <p className="text-xs text-gray-500 font-mono">{serviceRequest.id}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/admin/service-requests/${serviceRequest.id}`)}
                          className="text-sm"
                        >
                          Open Service Request →
                        </Button>
                        <Button
                          onClick={handleRequestScopeReview}
                          disabled={actionInProgress === 'scope'}
                          className="bg-orange-500 hover:bg-orange-600 text-white text-sm"
                        >
                          Request Scope Review
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                        <p className="text-xs uppercase font-bold text-gray-600 tracking-widest">Service Details</p>
                        <div>
                          <p className="text-xs text-gray-600 font-semibold mb-1">Service Category</p>
                          <p className="text-sm font-semibold text-gray-900">{serviceRequest.service_category_name || serviceRequest.service_category || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-semibold mb-1">Service Type</p>
                          <p className="text-sm font-semibold text-gray-900">{serviceRequest.service_name || serviceRequest.service_type || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-semibold mb-1">Property Type</p>
                          <p className="text-sm font-semibold text-gray-900">{serviceRequest.property_type || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-semibold mb-1">Postcode</p>
                          <p className="text-sm font-semibold text-gray-900">{serviceRequest.postcode || '—'}</p>
                        </div>
                        <div className="border-t pt-4">
                          <p className="text-xs text-gray-600 font-semibold mb-1">Quoted Price</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {serviceRequest.quoted_price != null
                              ? `£${Number(serviceRequest.quoted_price).toFixed(2)}`
                              : '—'}
                          </p>
                          {serviceRequest.quoted_date && (
                            <p className="text-xs text-gray-500 mt-1">
                              Quoted on {new Date(serviceRequest.quoted_date).toLocaleDateString('en-GB')}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                        <p className="text-xs uppercase font-bold text-gray-600 tracking-widest">Customer Contact</p>
                        <div>
                          <p className="text-xs text-gray-600 font-semibold mb-1">Name</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {serviceRequest.contact_name || serviceRequest.customer_name_full || '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-semibold mb-1">Email</p>
                          <p className="text-sm font-semibold text-gray-900 break-all">{serviceRequest.contact_email || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-semibold mb-1">Phone</p>
                          <p className="text-sm font-semibold text-gray-900">{serviceRequest.contact_phone || '—'}</p>
                        </div>
                        {serviceRequest.company && (
                          <div>
                            <p className="text-xs text-gray-600 font-semibold mb-1">Company</p>
                            <p className="text-sm font-semibold text-gray-900">{serviceRequest.company}</p>
                          </div>
                        )}
                        <div className="border-t pt-4">
                          <p className="text-xs text-gray-600 font-semibold mb-1">Status</p>
                          <span className="inline-block px-3 py-1 bg-teal-50 text-teal-700 text-xs font-bold uppercase rounded">
                            {serviceRequest.status || 'pending'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {serviceRequest.details && (
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <p className="text-xs uppercase font-bold text-gray-600 tracking-widest mb-3">Request Details</p>
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">
                          {(() => {
                            try {
                              const parsed = JSON.parse(serviceRequest.details);
                              return JSON.stringify(parsed, null, 2);
                            } catch { return serviceRequest.details; }
                          })()}
                        </pre>
                      </div>
                    )}

                    {serviceRequest.notes && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <p className="text-xs uppercase font-bold text-gray-600 tracking-widest mb-2">Internal Notes</p>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{serviceRequest.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'scope' && !serviceRequest && job.scope && (
                  <div className="grid grid-cols-3 gap-8">
                    {/* Left & Middle Columns */}
                    <div className="col-span-2 space-y-8">
                      {/* Platform Confirmed Scope */}
                      <div className="flex items-center justify-between pb-6 border-b border-gray-200">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-teal-600" />
                          <h3 className="text-lg font-bold text-gray-900">Platform confirmed scope</h3>
                        </div>
                        <Button
                          onClick={handleRequestScopeReview}
                          disabled={actionInProgress === 'scope'}
                          className="bg-orange-500 hover:bg-orange-600 text-white text-sm"
                        >
                          Request Scope Review
                        </Button>
                      </div>

                      {/* Scope Comparison */}
                      <div className="grid grid-cols-2 gap-6">
                        {/* Original Scope */}
                        <div className="bg-gray-50 rounded-lg p-6">
                          <div className="mb-6">
                            <p className="text-xs uppercase font-bold text-gray-600 mb-2">ORIGINAL SCOPE</p>
                            <p className="text-xs text-gray-500">{job.scope.originalScope.initialLocation}</p>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <p className="text-xs text-gray-600 font-semibold mb-1">Primary Task</p>
                              <p className="text-sm font-semibold text-gray-900">{job.scope.originalScope.primaryTask}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 font-semibold mb-1">Allocated Duration</p>
                              <p className="text-sm font-semibold text-gray-900">{job.scope.originalScope.allocatedDuration}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 font-semibold mb-1">Materials Budget</p>
                              <p className="text-sm font-semibold text-gray-900">{job.scope.originalScope.materialsBudget}</p>
                            </div>
                            <div className="border-t pt-4">
                              <p className="text-xs text-gray-600 font-semibold mb-1">TOTAL ESTIMATED</p>
                              <p className="text-2xl font-bold text-gray-900">{job.scope.originalScope.totalEstimated}</p>
                            </div>
                          </div>
                        </div>

                        {/* Adjusted Scope */}
                        <div className="bg-white border-2 border-teal-200 rounded-lg p-6 relative">
                          <div className="absolute top-4 right-4">
                            <span className="inline-block bg-teal-600 text-white text-xs uppercase font-bold px-2 py-1 rounded">
                              LIVE ADJUSTMENT
                            </span>
                          </div>

                          <div className="mb-6">
                            <p className="text-xs uppercase font-bold text-gray-600 mb-2 flex items-center gap-2">
                              ADJUSTED SCOPE
                              {job.scope.adjustedScope.verified && (
                                <>
                                  <span className="flex items-center gap-1 text-teal-600">
                                    <CheckCircle className="w-4 h-4" />
                                    Verified
                                  </span>
                                </>
                              )}
                            </p>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <p className="text-xs text-gray-600 font-semibold mb-1">Primary Task</p>
                              <p className="text-sm font-semibold text-gray-900">{job.scope.adjustedScope.primaryTask}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 font-semibold mb-1">Allocated Duration</p>
                              <div className="flex items-baseline gap-2">
                                <p className="text-sm font-semibold text-gray-900">{job.scope.adjustedScope.allocatedDuration}</p>
                                <span className="text-xs text-teal-600 font-semibold">{job.scope.adjustedScope.durationIncrease}</span>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 font-semibold mb-1">Materials Budget</p>
                              <div className="flex items-baseline gap-2">
                                <p className="text-sm font-semibold text-gray-900">{job.scope.adjustedScope.materialsBudget}</p>
                                <span className="text-xs text-teal-600 font-semibold">{job.scope.adjustedScope.budgetIncrease}</span>
                              </div>
                            </div>
                            <div className="border-t pt-4">
                              <p className="text-xs text-gray-600 font-semibold mb-1">NEW ADJUSTED TOTAL</p>
                              <p className="text-2xl font-bold text-teal-600">{job.scope.adjustedScope.newAdjustedTotal}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Adjustment Notes */}
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h4 className="text-xs uppercase font-bold text-gray-600 mb-6">ADJUSTMENT NOTES</h4>
                        <div className="space-y-6">
                          {job.scope.adjustmentNotes.map((note, idx) => (
                            <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 bg-gray-300 rounded-full flex-shrink-0"></div>
                                <div className="flex-1">
                                  <div className="flex items-baseline justify-between mb-2">
                                    <p className="text-sm font-semibold text-gray-900">{note.author}</p>
                                    <p className="text-xs text-gray-600">
                                      {note.date}, {note.time}
                                    </p>
                                  </div>
                                  <p className="text-xs text-gray-600 mb-2">{note.role}</p>
                                  <p className="text-sm text-gray-700 leading-relaxed">{note.note}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Right Sidebar */}
                    <div className="space-y-6">
                      {/* Service Details */}
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h4 className="text-xs uppercase font-bold text-gray-600 mb-4">SERVICE DETAILS</h4>
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <MapPin className="w-4 h-4 text-gray-600" />
                              <p className="text-xs text-gray-600 font-semibold">Job Location</p>
                            </div>
                            <p className="text-sm font-semibold text-gray-900">{job.scope.jobLocation}</p>
                          </div>
                          <div className="border-t pt-4">
                            <div className="flex items-center gap-2 mb-1">
                              <FileText className="w-4 h-4 text-gray-600" />
                              <p className="text-xs text-gray-600 font-semibold">Primary Contact</p>
                            </div>
                            <p className="text-sm font-semibold text-gray-900">{job.scope.primaryContact}</p>
                          </div>
                          <div className="border-t pt-4">
                            <p className="text-xs text-gray-600 font-semibold mb-1">Warranty Scope</p>
                            <p className="text-sm font-semibold text-gray-900">{job.scope.warrantyScope}</p>
                          </div>
                        </div>
                      </div>

                      {/* Inspection Media */}
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h4 className="text-xs uppercase font-bold text-gray-600 mb-4">INSPECTION MEDIA ({job.scope.inspectionMedia.length})</h4>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          {job.scope.inspectionMedia.map((photo, idx) => (
                            <div key={idx} className="relative group">
                              <img
                                src={photo}
                                alt={`Inspection ${idx + 1}`}
                                className="w-full h-24 rounded-lg object-cover"
                              />
                              {idx === job.scope.inspectionMedia.length - 1 && (
                                <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center text-white font-semibold">
                                  +2 More
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <button className="w-full border-2 border-dashed border-gray-300 rounded-lg py-8 text-center hover:bg-white transition-colors">
                          <ImageIcon className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                          <p className="text-xs text-gray-600 font-semibold">UPLOAD</p>
                        </button>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-2">
                        <Button
                          onClick={() => handleScopeDecision('approve')}
                          disabled={actionInProgress === 'scopeDecision'}
                          className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                        >
                          Approve Adjusted Scope
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleScopeDecision('flag-audit')}
                          disabled={actionInProgress === 'scopeDecision'}
                          className="w-full text-gray-700 border-gray-300"
                        >
                          Flag for Audit
                        </Button>
                        <Button
                          onClick={() => handleScopeDecision('reject')}
                          disabled={actionInProgress === 'scopeDecision'}
                          className="w-full bg-white text-red-600 border-2 border-red-600 hover:bg-red-50"
                        >
                          Reject Adjustments
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'variations' && job.variations && (
                  <div className="space-y-8">
                    {/* Top Section - 3 Column Layout */}
                    <div className="grid grid-cols-2 gap-8">
                      {/* Left Column - Total Impact */}
                      <div className="bg-gray-50 rounded-lg p-8">
                        <div className="inline-block bg-teal-100 text-teal-700 text-xs font-bold px-3 py-1 rounded mb-6">
                          TOTAL IMPACT
                        </div>
                        <div className="mb-8">
                          <p className="text-6xl font-bold text-gray-900 leading-tight">
                            +${(job.variations.totalImpact / 100).toFixed(2)}<span className="text-3xl">.00</span>
                          </p>
                        </div>
                        <p className="text-sm text-gray-700 mb-8 leading-relaxed">
                          Accumulated requested variations for Job #{job.id.replace('JOB-', '').replace('JV-', '')}
                        </p>
                        <div className="space-y-6 border-t pt-6">
                          <div>
                            <p className="text-xs uppercase font-semibold text-gray-600 mb-2">APPROVED</p>
                            <p className="text-2xl font-bold text-gray-900">£{(job.variations.approved / 100).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase font-semibold text-gray-600 mb-2">PENDING</p>
                            <p className="text-2xl font-bold text-gray-900">£{(job.variations.pending / 100).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Middle Column - Variation Timeline */}
                      <div className="relative">
                        {/* Vertical Timeline Line */}
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 via-blue-500 via-green-500 to-red-500 rounded-full"></div>

                        {/* Variation Cards */}
                        <div className="space-y-0 pl-12">
                          {job.variations.requests.map((req, idx) => {
                            const statusColor = 
                              req.status === 'Submitted' ? { bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-400', icon: 'text-orange-600' } :
                              req.status === 'Under Review' ? { bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500', icon: 'text-blue-600' } :
                              req.status === 'Approved' ? { bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-500', icon: 'text-green-600' } :
                              { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', icon: 'text-red-600' };

                            return (
                              <div key={idx} className="relative mb-0">
                                {/* Timeline Dot */}
                                <div className={`absolute -left-14 top-6 w-4 h-4 ${statusColor.dot} rounded-full border-4 border-white`}></div>
                                
                                {/* Card */}
                                <div className={`${statusColor.bg} border ${statusColor.border} rounded-lg p-4 mb-4`}>
                                  <div className="flex items-start gap-3 mb-3">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${statusColor.bg} border ${statusColor.border}`}>
                                      {req.icon === 'document' && <FileText className={`w-4 h-4 ${statusColor.icon}`} />}
                                      {req.icon === 'image' && <ImageIcon className={`w-4 h-4 ${statusColor.icon}`} />}
                                      {req.icon === 'checkmark' && <CheckCircle className={`w-4 h-4 ${statusColor.icon}`} />}
                                      {req.icon === 'x' && <AlertCircle className={`w-4 h-4 ${statusColor.icon}`} />}
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-xs font-bold text-gray-900">{req.id}</p>
                                      <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded mt-1 ${
                                        req.status === 'Submitted' ? 'bg-orange-200 text-orange-800' :
                                        req.status === 'Under Review' ? 'bg-blue-200 text-blue-800' :
                                        req.status === 'Approved' ? 'bg-green-200 text-green-800' :
                                        'bg-red-200 text-red-800'
                                      }`}>
                                        {req.status === 'Submitted' && '● Submitted'}
                                        {req.status === 'Under Review' && '◐ Under Review'}
                                        {req.status === 'Approved' && '✓ Approved'}
                                        {req.status === 'Rejected' && '✕ Rejected'}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <p className="text-sm font-semibold text-gray-900 mb-1">{req.title}</p>
                                  <p className="text-xs text-gray-600 mb-3">{req.description}</p>
                                  
                                  <div className="flex items-center justify-between text-xs mb-3">
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <Calendar className="w-3 h-3" />
                                      {req.date}
                                    </div>
                                    <span className="font-semibold text-gray-900">+${(req.amount / 100).toFixed(2)}</span>
                                  </div>

                                  {(req.actionButton || req.managedBy || req.actionLabel) && (
                                    <div className="border-t border-gray-200 pt-2">
                                      {req.actionButton && (
                                        <Button
                                          onClick={() => handleDynamicAction(req)}
                                          className="w-full bg-teal-700 hover:bg-teal-800 text-white text-xs h-8 py-1"
                                        >
                                          {req.actionButton}
                                        </Button>
                                      )}
                                      {req.managedBy && (
                                        <p className="text-xs text-gray-600 text-center py-1">Managed by {req.managedBy}</p>
                                      )}
                                      {req.actionLabel && (
                                        <p className="text-xs font-semibold text-center py-1" style={{color: '#dc2626'}}>
                                          {req.actionLabel}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Bottom Section - Statistics & Chart */}
                    <div className="bg-gray-50 rounded-lg p-8">
                      <div className="flex items-center justify-between gap-8">
                        {/* Donut Chart */}
                        <div className="flex-shrink-0">
                          <div className="relative w-40 h-40">
                            {/* Donut Chart */}
                            <svg viewBox="0 0 140 140" className="w-full h-full">
                              {/* Background circle */}
                              <circle cx="70" cy="70" r="55" fill="none" stroke="#e5e7eb" strokeWidth="14" />
                              
                              {/* Green segment - 25% */}
                              <path
                                d="M 70 15 A 55 55 0 0 1 113.88 26.12"
                                fill="none"
                                stroke="#10b981"
                                strokeWidth="14"
                                strokeLinecap="round"
                              />
                              
                              {/* Blue segment - 50% */}
                              <path
                                d="M 113.88 26.12 A 55 55 0 0 1 70 125"
                                fill="none"
                                stroke="#3b82f6"
                                strokeWidth="14"
                                strokeLinecap="round"
                              />
                              
                              {/* Orange segment - 12.5% */}
                              <path
                                d="M 70 125 A 55 55 0 0 1 41.58 108.78"
                                fill="none"
                                stroke="#f59e0b"
                                strokeWidth="14"
                                strokeLinecap="round"
                              />
                              
                              {/* Red segment - 12.5% */}
                              <path
                                d="M 41.58 108.78 A 55 55 0 0 1 70 15"
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth="14"
                                strokeLinecap="round"
                              />
                            </svg>
                            
                            {/* Center text */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <p className="text-4xl font-bold text-gray-900">4</p>
                              <p className="text-xs text-gray-600 font-semibold mt-1">TOTAL</p>
                            </div>
                          </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="flex-1 grid grid-cols-3 gap-8">
                          <div>
                            <p className="text-xs uppercase font-bold text-gray-600 mb-2">APPROVED</p>
                            <p className="text-lg font-bold text-gray-900">1 Request</p>
                            <p className="text-sm text-gray-600">£{(job.variations.approved / 100).toFixed(2)} total</p>
                          </div>

                          <div>
                            <p className="text-xs uppercase font-bold text-gray-600 mb-2">PENDING</p>
                            <p className="text-lg font-bold text-gray-900">2 Requests</p>
                            <p className="text-sm text-gray-600">£{(job.variations.pending / 100).toFixed(2)} total</p>
                          </div>

                          <div>
                            <p className="text-xs uppercase font-bold text-gray-600 mb-2">REJECTED</p>
                            <p className="text-lg font-bold text-gray-900">1 Request</p>
                            <p className="text-sm text-gray-600">£{(job.variations.rejected / 100).toFixed(2)} total</p>
                          </div>
                        </div>

                        {/* Export Button */}
                        <Button
                          onClick={handleExportVariationHistory}
                          className="flex-shrink-0 bg-white border-2 border-gray-300 text-teal-700 hover:bg-gray-50 font-semibold text-sm h-12"
                        >
                          ⬇ Export Variation<br/>History
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'finance' && (
                  <div className="grid grid-cols-3 gap-8">
                    {/* Left Column - Financial Breakdown */}
                    <div className="col-span-2 space-y-6">
                      {/* Info Alert */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-semibold text-blue-900">Financial Data</p>
                          <p className="text-blue-700 text-xs mt-1">Showing job valuation. Full financial breakdown including variations and payouts available when detailed quote data is loaded.</p>
                        </div>
                      </div>

                      {/* Financial Breakdown Section */}
                      <div className="bg-white rounded-lg border border-gray-200 p-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-8">Job Value Summary</h3>
                        
                        {/* Base Contract Value */}
                        <div className="bg-gray-50 rounded-lg p-6 mb-6">
                          <p className="text-xs uppercase font-semibold text-gray-600 mb-4">JOB VALUE</p>
                          <div className="flex items-baseline justify-between gap-4 mb-6">
                            <p className="text-lg font-semibold text-gray-900">{job.service}</p>
                            <p className="text-3xl font-bold text-gray-900">{job.value}</p>
                          </div>

                          {/* Job Details */}
                          <div className="space-y-4 border-t pt-6">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-700">Customer</p>
                              <p className="text-sm font-semibold text-gray-900">{job.customer}</p>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-700">Provider</p>
                              <p className="text-sm font-semibold text-gray-900">{job.provider}</p>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-700">Status</p>
                              <p className="text-sm font-semibold text-gray-900">{job.status}</p>
                            </div>
                          </div>

                          {/* Total Value */}
                          <div className="border-t pt-6 mt-6 flex items-center justify-between">
                            <p className="text-sm font-bold text-gray-900">Total Job Value</p>
                            <p className="text-2xl font-bold text-teal-600">{job.value}</p>
                          </div>
                        </div>

                        {/* Scheduled Details */}
                        <div className="bg-gray-50 rounded-lg p-6">
                          <p className="text-xs uppercase font-semibold text-gray-600 mb-4">SCHEDULED</p>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-700">Scheduled Date & Time</p>
                              <p className="text-sm font-semibold text-gray-900">{job.scheduled}</p>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-700">Location</p>
                              <p className="text-sm font-semibold text-gray-900">{job.location}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Actions & Details */}
                    <div className="space-y-6">
                      {/* Summary */}
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <p className="text-xs uppercase font-bold text-gray-600 mb-4 flex items-center gap-2">
                          <FileText className="w-4 h-4" /> JOB SUMMARY
                        </p>
                        <div className="space-y-4 text-sm">
                          <div>
                            <p className="text-xs text-gray-600 font-semibold mb-1">Service</p>
                            <p className="text-gray-900">{job.service}</p>
                          </div>
                          <div className="border-t pt-3">
                            <p className="text-xs text-gray-600 font-semibold mb-1">Property</p>
                            <p className="text-gray-900">{job.property}</p>
                          </div>
                          <div className="border-t pt-3">
                            <p className="text-xs text-gray-600 font-semibold mb-1">Estimated Duration</p>
                            <p className="text-gray-900">{job.estimatedDuration}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'notes' && (
                  <div className="grid grid-cols-3 gap-8">
                    {/* Left Column - Notes */}
                    <div className="col-span-2 space-y-6">
                      {/* Internal Admin Note Input */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-xs uppercase font-bold text-gray-600 mb-4">INTERNAL ADMIN NOTE</h4>
                        <textarea
                          value={noteContent}
                          onChange={(e) => setNoteContent(e.target.value)}
                          placeholder="Add internal note..."
                          className="w-full h-24 border border-gray-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex gap-4 items-center">
                            <input
                              type="file"
                              data-note-attach-input
                              className="hidden"
                              onChange={handleUploadDocument}
                            />
                            <button
                              type="button"
                              onClick={handleNoteAttachment}
                              title="Attach a file"
                              className="text-gray-400 hover:text-gray-600 text-lg"
                            >
                              📎
                            </button>
                            <button
                              type="button"
                              onClick={handleNoteMention}
                              title="Mention someone"
                              className="text-gray-400 hover:text-gray-600 text-lg"
                            >
                              @
                            </button>
                          </div>
                          <Button 
                            onClick={handleSaveNote}
                            disabled={isSavingNote || !noteContent.trim()}
                            className="bg-teal-700 hover:bg-teal-800 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {isSavingNote ? 'Saving...' : 'Save Note'}
                          </Button>
                        </div>
                      </div>

                      {/* Notes Feed */}
                      <div className="space-y-6">
                        {job?.notes && job.notes.length > 0 ? (
                          job.notes.map((note) => (
                            <div key={note.id} className="space-y-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-full mt-1 ${
                                  note.role === 'Admin' || note.role.includes('Admin') ? 'bg-teal-500' : 'bg-gray-300'
                                }`}></div>
                                <p className="text-sm font-bold text-gray-900">{note.author}</p>
                                <p className="text-xs text-gray-600">{note.date}, {note.time}</p>
                              </div>
                              <div className={`ml-6 rounded-lg p-4 border ${
                                note.role === 'Admin' || note.role.includes('Admin') ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'
                              } space-y-3`}>
                                <p className="text-sm text-gray-700">{note.content}</p>
                                {note.tags && note.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {note.tags.map((tag, idx) => (
                                      <span key={idx} className="inline-block bg-amber-100 text-amber-700 text-xs font-bold px-2 py-1 rounded">
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {note.attachments && note.attachments.length > 0 && (
                                  <div className="space-y-2">
                                    {note.attachments.map((attachment, idx) => (
                                      <div key={idx} className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                        <FileText className="w-4 h-4" />
                                        <span>{attachment.name}</span>
                                        <span className="text-gray-500">{attachment.size}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-8 text-center">
                            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-600 text-sm">No notes added yet</p>
                            <p className="text-gray-500 text-xs mt-1">Add your first note using the form above</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column - Sidebar */}
                    <div className="space-y-6">
                      {/* Internal Snapshot */}
                      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <h4 className="text-xs uppercase font-bold text-gray-600 mb-6">JOB STATS</h4>
                        <div className="space-y-6">
                          {/* Status */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <CheckCircle className="w-5 h-5 text-teal-600" />
                              <div>
                                <p className="text-xs text-gray-600 font-semibold">Current Status</p>
                                <p className="text-xs text-gray-500">Job progress</p>
                              </div>
                            </div>
                            <p className="text-sm font-bold text-gray-900">{job?.status || 'N/A'}</p>
                          </div>

                          {/* Notes Count */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-teal-600" />
                              <div>
                                <p className="text-xs text-gray-600 font-semibold">Total Notes</p>
                                <p className="text-xs text-gray-500">In this job</p>
                              </div>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{job?.notes?.length || 0}</p>
                          </div>

                          {/* Admin Access */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-5 h-5 text-teal-600">👥</div>
                              <div>
                                <p className="text-xs text-gray-600 font-semibold">Service</p>
                                <p className="text-xs text-gray-500">Job type</p>
                              </div>
                            </div>
                            <p className="text-sm font-bold text-gray-900">{job?.service || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Location Info */}
                      <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                        <h4 className="text-xs uppercase font-bold text-gray-600 p-6 pb-4">LOCATION</h4>
                        <div className="h-40 bg-gray-300 flex items-center justify-center relative">
                          <MapPin className="w-8 h-8 text-teal-600" />
                        </div>
                        <div className="p-4 bg-white flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-900">{job?.location || 'Location not specified'}</p>
                          <button
                            onClick={handleOpenLocation}
                            title="Open in Google Maps"
                            className="text-teal-600 hover:text-teal-700"
                          >
                            📍
                          </button>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-3">
                        <Button
                          onClick={handleEditNotes}
                          className="w-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium py-2"
                        >
                          ✎ Edit Notes
                        </Button>
                        <Button
                          onClick={handleExportNotes}
                          className="w-full bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium py-2"
                        >
                          📋 Export Notes
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'photos' && (
                  <div className="grid grid-cols-3 gap-8">
                    {/* Left Column - Documentation & Media */}
                    <div className="col-span-2 space-y-8">
                      {/* Official Documentation */}
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs uppercase font-semibold text-gray-600 mb-1">OFFICIAL DOCUMENTATION</p>
                            <h3 className="text-2xl font-bold text-gray-900">Contractual Records</h3>
                          </div>
                          <Button 
                            onClick={handleUploadDocumentClick}
                            disabled={isUploadingDocument}
                            className="bg-teal-700 hover:bg-teal-800 text-white flex items-center gap-2"
                          >
                            {isUploadingDocument ? 'Uploading...' : '📄 Upload New Document'}
                          </Button>
                        </div>

                        {/* Document Cards - Dynamic Content */}
                        <div className="grid grid-cols-3 gap-6">
                          {job?.documents && job.documents.length > 0 ? (
                            job.documents.map((doc: any, idx: number) => (
                              <div key={idx} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                <div className="flex items-center justify-between mb-4">
                                  <FileText className="w-8 h-8 text-blue-500" />
                                  <span className={`inline-block text-xs font-bold px-2 py-1 rounded ${
                                    doc.status === 'signed' ? 'bg-green-100 text-green-700' :
                                    doc.status === 'paid' ? 'bg-blue-100 text-blue-700' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>
                                    ● {doc.status?.toUpperCase() || 'PENDING'}
                                  </span>
                                </div>
                                <h4 className="text-sm font-bold text-gray-900 mb-4">{doc.filename || 'Document'}</h4>
                                <p className="text-xs text-gray-600 mb-2">
                                  {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'Date unknown'}
                                </p>
                                <p className="text-xs text-gray-500 mb-4">{doc.file_size || 'Size unknown'}</p>
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    onClick={() => handleDocView(doc)}
                                    className="flex-1 text-teal-600 text-xs h-8"
                                  >
                                    👁 View
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    onClick={() => handleDocDownload(doc)}
                                    className="flex-1 text-gray-600 text-xs h-8"
                                  >
                                    ⬇ Download
                                  </Button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="col-span-3 bg-gray-50 rounded-lg p-8 text-center border border-dashed border-gray-300">
                              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                              <p className="text-gray-600">No documents uploaded yet</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Site Media */}
                      <div className="space-y-6">
                        <div>
                          <p className="text-xs uppercase font-semibold text-gray-600 mb-1">SITE MEDIA</p>
                          <h3 className="text-2xl font-bold text-gray-900">Visual Progress Tracker</h3>
                        </div>

                        {/* Photo Grid - Dynamic Content */}
                        {job?.photos && job.photos.length > 0 ? (
                          <div className="grid grid-cols-4 gap-4">
                            {job.photos.map((photo: any, idx: number) => (
                              <div key={idx} className="relative group rounded-lg overflow-hidden bg-gray-200 h-40 cursor-pointer">
                                {photo.url ? (
                                  <>
                                    <img src={photo.url} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                                    <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">{photo.type || 'PHOTO'}</div>
                                  </>
                                ) : (
                                  <div className="absolute inset-0 bg-gray-300 flex items-center justify-center">
                                    <span className="text-xs font-bold text-white bg-black/50 px-3 py-1 rounded">{photo.type?.toUpperCase() || 'PHOTO'}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                            <div className="rounded-lg border-2 border-dashed border-gray-300 h-40 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50">
                              <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                              <p className="text-xs text-gray-600 font-semibold">ADD PHOTOS</p>
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                            <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-600 font-medium">No photos uploaded yet</p>
                            <p className="text-xs text-gray-500 mt-1">Upload progress photos to track job completion</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column - Job Overview & Activity */}
                    <div className="space-y-6">
                      {/* Job Overview */}
                      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <p className="text-xs uppercase font-semibold text-gray-600 mb-4">JOB OVERVIEW</p>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">Status</p>
                            <span className={`inline-block text-xs font-bold px-3 py-1 rounded ${
                              job?.status === 'completed' ? 'bg-green-100 text-green-700' :
                              job?.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {job?.status?.replace('_', ' ').toUpperCase() || 'Unknown'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">Service</p>
                            <p className="text-sm font-bold text-gray-900">{job?.service || 'N/A'}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">Provider</p>
                            <p className="text-sm font-bold text-gray-900">{job?.provider || 'Unassigned'}</p>
                          </div>
                          <div className="border-t pt-4">
                            <p className="text-xs text-gray-600 font-semibold mb-2">Job Value</p>
                            <p className="text-lg font-bold text-teal-600">{job?.value || '£0.00'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Recent Activity */}
                      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <p className="text-xs uppercase font-semibold text-gray-600 mb-4">JOB DETAILS</p>
                        <div className="space-y-3 text-sm">
                          <div>
                            <p className="text-xs text-gray-600 font-semibold mb-1">Customer</p>
                            <p className="text-gray-900 font-medium">{job?.customer || 'Unknown'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 font-semibold mb-1">Location</p>
                            <p className="text-gray-900 font-medium">{job?.location || 'Not specified'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 font-semibold mb-1">Scheduled Date</p>
                            <p className="text-gray-900 font-medium">{job?.scheduled || 'TBD'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab !== 'overview' && activeTab !== 'timeline' && activeTab !== 'scope' && activeTab !== 'variations' && activeTab !== 'finance' && activeTab !== 'notes' && activeTab !== 'photos' && (
                  <div className="py-12 text-center">
                    <p className="text-gray-600">Content for {activeTab} will be displayed here</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>

      {/* Assign Provider Modal */}
      <AssignProviderModal
        jobId={job?.id || ''}
        serviceType={job?.service || ''}
        location={job?.location || ''}
        scheduledTime={job?.scheduled || ''}
        priority="High"
        isOpen={isAssignProviderOpen}
        onClose={() => setIsAssignProviderOpen(false)}
        onAssign={handleAssignProviderSubmit}
      />

      {/* Reassign Provider Modal */}
      <AssignProviderModal
        jobId={job?.id || ''}
        serviceType={job?.service || ''}
        location={job?.location || ''}
        scheduledTime={job?.scheduled || ''}
        priority="High"
        isOpen={isReassignProviderOpen}
        onClose={() => setIsReassignProviderOpen(false)}
        onAssign={handleReassignProviderSubmit}
      />

      {/* Hidden File Input for Document Upload */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleUploadDocument}
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.xlsx,.xls,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
      />
    </>
  );
};

export default JobDetailsPage;
